import React, {useContext, useEffect, useRef, useState, useCallback} from "react";
import ReactDOM from "react-dom";
import "./StartupProjects.scss";
import {bigProjects} from "../../portfolio";
import StyleContext from "../../contexts/StyleContext";

function openUrlInNewTab(url) {
  if (!url) return;
  const win = window.open(url, "_blank");
  if (win) win.focus();
}

function shortName(name) {
  const cut = name.indexOf(" (");
  if (cut > 0) return name.slice(0, cut);
  const slash = name.indexOf(" / ");
  if (slash > 0) return name.slice(0, slash);
  return name;
}

export default function StartupProject() {
  const {isDark} = useContext(StyleContext);
  const railRef = useRef(null);
  const stageRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [stageKey, setStageKey] = useState(0);
  const [direction, setDirection] = useState(0); // -1 prev, 1 next
  const activeIndexRef = useRef(0);

  const projects = bigProjects.projects;
  const active = projects[activeIndex] || projects[0];
  const canPrev = activeIndex > 0;
  const canNext = activeIndex < projects.length - 1;
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // Prefetch adjacent media for snappy switches
  useEffect(() => {
    const list = bigProjects.projects || [];
    const targets = [list[activeIndex - 1], list[activeIndex + 1]].filter(
      Boolean
    );
    targets.forEach(project => {
      const src = project.videoPoster || project.image;
      if (!src) return;
      const img = new Image();
      img.src = src;
    });
  }, [activeIndex]);

  const selectProject = useCallback(
    (i, dir = 0) => {
      if (i < 0 || i >= projects.length) return;
      if (i === activeIndexRef.current) return;

      const inferred =
        dir ||
        (i > activeIndexRef.current ? 1 : i < activeIndexRef.current ? -1 : 0);
      setDirection(inferred);
      setActiveIndex(i);
      setStageKey(k => k + 1);
      setLightboxOpen(false);
      setLoadError(false);

      const rail = railRef.current;
      if (!rail) return;
      const thumbs = rail.querySelectorAll(".project-thumb");
      const thumb = thumbs[i];
      if (thumb) {
        thumb.scrollIntoView({
          behavior: "auto",
          inline: "center",
          block: "nearest"
        });
      }
    },
    [projects.length]
  );

  const goPrev = useCallback(() => {
    selectProject(activeIndexRef.current - 1, -1);
  }, [selectProject]);

  const goNext = useCallback(() => {
    selectProject(activeIndexRef.current + 1, 1);
  }, [selectProject]);

  useEffect(() => {
    const onKey = e => {
      const section = document.getElementById("projects");
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const inView =
        rect.top < window.innerHeight * 0.85 &&
        rect.bottom > window.innerHeight * 0.15;
      if (!inView) return;
      if (lightboxOpen) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, lightboxOpen]);

  useEffect(() => {
    const onResize = () => {
      const rail = railRef.current;
      if (!rail) return;
      const thumbs = rail.querySelectorAll(".project-thumb");
      const thumb = thumbs[activeIndexRef.current];
      if (thumb) {
        thumb.scrollIntoView({inline: "center", block: "nearest"});
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Swipe on featured stage
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onTouchStart = e => {
      if (!e.touches[0]) return;
      tracking = true;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const onTouchEnd = e => {
      if (!tracking || !e.changedTouches[0]) return;
      tracking = false;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      if (dx < 0) goNext();
      else goPrev();
    };

    el.addEventListener("touchstart", onTouchStart, {passive: true});
    el.addEventListener("touchend", onTouchEnd, {passive: true});
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [goNext, goPrev, stageKey]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = e => {
      if (e.key === "Escape") setLightboxOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen]);

  if (!bigProjects.display || !active) return null;

  const hasVideo = Boolean(active.videoDriveId || active.video);
  const poster = active.videoPoster || active.image;
  const drivePreviewUrl = active.videoDriveId
    ? `https://drive.google.com/file/d/${active.videoDriveId}/preview`
    : null;
  const driveOpenUrl = active.videoDriveId
    ? `https://drive.google.com/file/d/${active.videoDriveId}/view`
    : active.video;
  const slideClass =
    direction > 0
      ? " is-slide-next"
      : direction < 0
      ? " is-slide-prev"
      : "";

  const openDemo = () => {
    if (!hasVideo) return;
    setLoadError(false);
    setLightboxOpen(true);
  };

  const closeDemo = () => setLightboxOpen(false);

  const lightbox =
    lightboxOpen && hasVideo
      ? ReactDOM.createPortal(
          <div
            className="project-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`${active.projectName} demo`}
            onClick={closeDemo}
          >
            <div
              className="project-lightbox-panel"
              onClick={ev => ev.stopPropagation()}
            >
              <header className="project-lightbox-header">
                <div>
                  <span className="project-lightbox-eyebrow">
                    Project demo
                  </span>
                  <h3>{active.projectName}</h3>
                </div>
                <button
                  type="button"
                  className="project-lightbox-close"
                  onClick={closeDemo}
                  aria-label="Close demo"
                >
                  ✕
                </button>
              </header>
              <div className="project-lightbox-stage">
                {loadError && (
                  <div className="project-lightbox-loading is-error">
                    <span>Couldn’t load the demo video.</span>
                    <a
                      className="project-lightbox-retry"
                      href={driveOpenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open on Google Drive
                    </a>
                  </div>
                )}
                {drivePreviewUrl ? (
                  <iframe
                    key={active.videoDriveId}
                    className="project-lightbox-embed"
                    src={drivePreviewUrl}
                    title={`${active.projectName} demo`}
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    loading="eager"
                    onError={() => setLoadError(true)}
                  />
                ) : (
                  <video
                    key={active.video}
                    className="project-lightbox-video is-ready"
                    src={`${active.video}#t=0.1`}
                    poster={poster || undefined}
                    controls
                    autoPlay
                    playsInline
                    preload="auto"
                    onError={() => setLoadError(true)}
                  >
                    <source src={active.video} type="video/mp4" />
                    Your browser does not support video playback.
                  </video>
                )}
              </div>
              <footer className="project-lightbox-footer">
                {active.footerLink &&
                  active.footerLink.map((link, linkIndex) => (
                    <button
                      key={linkIndex}
                      type="button"
                      className="project-link"
                      onClick={() => openUrlInNewTab(link.url)}
                    >
                      {link.name}
                      <span className="project-link-arrow" aria-hidden="true">
                        →
                      </span>
                    </button>
                  ))}
                {driveOpenUrl && (
                  <a
                    className="project-link project-link-demo"
                    href={driveOpenUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in Drive
                    <span className="project-link-arrow" aria-hidden="true">
                      ↗
                    </span>
                  </a>
                )}
              </footer>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <section
      className={`projects-showcase${isDark ? " is-dark" : ""}`}
      id="projects"
    >
      <div className="projects-showcase-inner">
        <header className="projects-header">
          <div className="projects-header-copy">
            <span className="section-eyebrow">Selected work</span>
            <h1 className="section-heading">{bigProjects.title}</h1>
            {bigProjects.subtitle && (
              <p className="section-subtitle">{bigProjects.subtitle}</p>
            )}
          </div>

          <div className="projects-nav">
            <span className="projects-progress-count" aria-live="polite">
              {String(activeIndex + 1).padStart(2, "0")} /{" "}
              {String(projects.length).padStart(2, "0")}
            </span>
            <div className="projects-nav-buttons">
              <button
                type="button"
                className="projects-nav-btn"
                onClick={goPrev}
                disabled={!canPrev}
                aria-label="Previous project"
              >
                ←
              </button>
              <button
                type="button"
                className="projects-nav-btn"
                onClick={goNext}
                disabled={!canNext}
                aria-label="Next project"
              >
                →
              </button>
            </div>
          </div>
        </header>

        <div
          className={`project-feature-shell tone-glow-${activeIndex % 4}`}
          ref={stageRef}
        >
          <div className="project-feature-glow" aria-hidden="true" />

          <article
            key={stageKey}
            className={`project-feature project-tone-${activeIndex % 4}${
              hasVideo ? " has-video" : ""
            }${slideClass}`}
            aria-live="polite"
          >
            <div
              className={`project-feature-media${
                hasVideo ? " is-clickable" : ""
              }`}
              onClick={hasVideo ? openDemo : undefined}
              onKeyDown={
                hasVideo
                  ? e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openDemo();
                      }
                    }
                  : undefined
              }
              role={hasVideo ? "button" : undefined}
              tabIndex={hasVideo ? 0 : undefined}
              aria-label={
                hasVideo ? `Watch ${active.projectName} demo` : undefined
              }
            >
              {hasVideo ? (
                <>
                  {poster && (
                    <img
                      src={poster}
                      alt=""
                      className="project-feature-poster"
                      decoding="async"
                    />
                  )}
                  <div className="project-feature-scrim" aria-hidden="true" />
                  <span className="project-play-btn" aria-hidden="true">
                    <span className="project-play-icon">▶</span>
                    <span>Watch demo</span>
                  </span>
                  {active.image && (
                    <img
                      src={active.image}
                      alt=""
                      className="project-feature-logo"
                      decoding="async"
                      aria-hidden="true"
                    />
                  )}
                </>
              ) : (
                active.image && (
                  <div className="project-feature-logo-wrap">
                    <div className="project-feature-mesh" aria-hidden="true" />
                    <img
                      src={active.image}
                      alt={active.projectName}
                      className="project-feature-logo-main"
                      decoding="async"
                    />
                  </div>
                )
              )}
            </div>

            <div className="project-feature-body">
              <div className="project-feature-meta">
                <span className="project-feature-index">
                  Project {String(activeIndex + 1).padStart(2, "0")}
                </span>
                {hasVideo && (
                  <span className="project-demo-chip">
                    <span className="project-demo-dot" aria-hidden="true" />
                    Demo
                  </span>
                )}
              </div>

              <h2 className="project-feature-title">{active.projectName}</h2>
              <p className="project-feature-desc">{active.projectDesc}</p>

              {active.tags && active.tags.length > 0 && (
                <ul className="project-tags" aria-label="Tech stack">
                  {active.tags.map(tag => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              )}

              <div className="project-card-links">
                {hasVideo && (
                  <button
                    type="button"
                    className="project-link project-link-primary"
                    onClick={openDemo}
                  >
                    Watch demo
                    <span className="project-link-arrow" aria-hidden="true">
                      ▶
                    </span>
                  </button>
                )}
                {active.footerLink &&
                  active.footerLink.map((link, linkIndex) => (
                    <button
                      key={linkIndex}
                      type="button"
                      className={`project-link${
                        !hasVideo && linkIndex === 0
                          ? " project-link-primary"
                          : ""
                      }`}
                      onClick={() => openUrlInNewTab(link.url)}
                    >
                      {link.name}
                      <span className="project-link-arrow" aria-hidden="true">
                        →
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          </article>
        </div>

        <div className="projects-rail-head">
          <span className="projects-rail-label">All projects</span>
          <span className="projects-rail-hint" aria-live="polite">
            {shortName(active.projectName)}
          </span>
        </div>

        <div className="projects-rail-wrap">
          <div
            ref={railRef}
            className="projects-rail"
            role="tablist"
            aria-label="Project list"
          >
            {projects.map((project, i) => {
              const thumbHasVideo = Boolean(
                project.videoDriveId || project.video
              );
              const thumbSrc = project.videoPoster || project.image;
              return (
                <button
                  key={project.projectName}
                  type="button"
                  role="tab"
                  aria-selected={i === activeIndex}
                  className={`project-thumb project-tone-${i % 4}${
                    i === activeIndex ? " is-active" : ""
                  }`}
                  onClick={() => selectProject(i)}
                >
                  <span className="project-thumb-media">
                    {thumbSrc ? (
                      <img
                        src={thumbSrc}
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    ) : null}
                    {thumbHasVideo && (
                      <span className="project-thumb-play" aria-hidden="true">
                        ▶
                      </span>
                    )}
                  </span>
                  <span className="project-thumb-copy">
                    <span className="project-thumb-index">
                      {String(i + 1).padStart(2, "0")}
                      {project.tags && project.tags[0]
                        ? ` · ${project.tags[0]}`
                        : ""}
                    </span>
                    <span className="project-thumb-name">
                      {shortName(project.projectName)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {lightbox}
    </section>
  );
}
